/**
 * ADMIN MODULE — Nurture Campaigns.
 * A grid of campaigns; click one to manage its steps (the emails). Each step
 * picks a reusable Template (format) + its own wait interval. Steps can be
 * added, copied to another campaign, drag-reordered, and test-sent.
 */

import { useEffect, useState, useCallback, useRef } from "react";
import {
  listCampaigns, createCampaign, deleteCampaign,
  listTemplates, saveTemplate, createStep, deleteStep, reorderSteps, copyStepToCampaign,
  sendTestTemplate, listFormats,
} from "../utils/nurtureService.js";
import { supabase } from "../../../shared/utils/supabaseClient.js";

const input = { width: "100%", boxSizing: "border-box", border: "1px solid var(--border)", borderRadius: 7, padding: "9px 11px", fontSize: 13.5, fontFamily: "inherit", marginTop: 5 };
const label = { fontSize: 12, fontWeight: 700, color: "var(--muted)", display: "block", marginTop: 14 };
function fmt(ts) { try { return ts ? new Date(ts).toLocaleString() : "—"; } catch { return ts; } }

// ── Campaign grid ────────────────────────────────────────────────────────────
function Grid({ campaigns, loading, onOpen, onNew, onDelete }) {
  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <p style={{ fontSize: 13, color: "var(--muted)", margin: 0 }}>Each campaign is a sequence of emails (steps). Click one to edit its steps.</p>
        <button className="btn" onClick={onNew}>+ New campaign</button>
      </div>
      {loading ? <div style={{ color: "var(--muted)" }}>Loading…</div> : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 16 }}>
          {campaigns.map((c) => (
            <div key={c.id} onClick={() => onOpen(c)} style={{ border: "1px solid var(--border)", borderRadius: 12, background: "var(--surface)", padding: 18, cursor: "pointer", position: "relative" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 8 }}>
                <div style={{ fontWeight: 800, fontSize: 16, color: "var(--heading)" }}>{c.name}</div>
                <span style={{ fontSize: 10, fontWeight: 700, color: c.status === "active" ? "var(--success)" : "var(--warn)", background: c.status === "active" ? "var(--success-soft)" : "var(--warn-soft)", padding: "2px 8px", borderRadius: 999 }}>{c.status}</span>
              </div>
              {c.description && <div style={{ fontSize: 12.5, color: "var(--muted)", marginTop: 6, lineHeight: 1.5 }}>{c.description}</div>}
              <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 12 }}>{c.step_count ?? 0} step{(c.step_count ?? 0) === 1 ? "" : "s"} · <code style={{ fontSize: 11 }}>{c.key}</code></div>
              {c.key !== "build-the-brand" && (
                <button onClick={(e) => { e.stopPropagation(); onDelete(c); }} title="Delete campaign" style={{ position: "absolute", top: 10, right: 10, border: "none", background: "none", color: "var(--muted)", cursor: "pointer", fontSize: 16, padding: 4 }}>✕</button>
              )}
            </div>
          ))}
          {campaigns.length === 0 && <div style={{ color: "var(--muted)" }}>No campaigns yet — create one.</div>}
        </div>
      )}
    </>
  );
}

// ── Step editor (right pane) ─────────────────────────────────────────────────
function StepEditor({ step, formats, campaigns, currentKey, isLast, busy, onSave, onDelete, onCopy, onTest, testTo, setTestTo }) {
  const [d, setD] = useState({ ...step });
  useEffect(() => { setD({ ...step }); }, [step]);
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));
  const [copyTo, setCopyTo] = useState("");

  return (
    <div style={{ border: "1px solid var(--border)", borderRadius: 12, padding: 20, background: "var(--surface)" }}>
      <div style={{ fontSize: 12.5, color: "var(--muted)" }}>
        Merge field <code style={{ background: "var(--sunken)", padding: "1px 6px", borderRadius: 4 }}>{"{{first_name}}"}</code> (falls back to "there"). Use <code style={{ background: "var(--sunken)", padding: "1px 6px", borderRadius: 4 }}>class="bc-btn"</code> on a link for the template's button color. The brand header/footer + unsubscribe are added by the template.
      </div>
      <label style={label}>Template (format)
        <select style={input} value={d.template_id || ""} onChange={(e) => set("template_id", e.target.value || null)}>
          <option value="">— Campaign default —</option>
          {formats.map((f) => <option key={f.id} value={f.id}>{f.name}{f.is_default ? " (default)" : ""}</option>)}
        </select>
      </label>
      <label style={label}>Subject<input style={input} value={d.subject || ""} onChange={(e) => set("subject", e.target.value)} /></label>
      <label style={label}>Preview text (inbox preheader)<input style={input} value={d.preview_text || ""} onChange={(e) => set("preview_text", e.target.value)} /></label>
      <label style={label}>HTML body<textarea style={{ ...input, minHeight: 180, fontFamily: "ui-monospace,Menlo,monospace", fontSize: 12.5 }} value={d.html || ""} onChange={(e) => set("html", e.target.value)} /></label>
      <label style={label}>Plain-text fallback<textarea style={{ ...input, minHeight: 100, fontFamily: "ui-monospace,Menlo,monospace", fontSize: 12.5 }} value={d.text || ""} onChange={(e) => set("text", e.target.value)} /></label>
      <div style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "center" }}>
        <label style={{ ...label, display: "flex", alignItems: "center", gap: 8 }}>
          <input type="checkbox" checked={!!d.is_active} onChange={(e) => set("is_active", e.target.checked)} style={{ width: "auto", marginTop: 0 }} /> Active
        </label>
        {!isLast && (
          <label style={{ ...label, display: "flex", alignItems: "center", gap: 8 }}>
            Wait <input type="number" min="0" value={d.interval_days ?? ""} onChange={(e) => set("interval_days", e.target.value)} style={{ ...input, width: 70, marginTop: 0 }} /> days before next
          </label>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 20, flexWrap: "wrap" }}>
        <button className="btn" onClick={() => onSave(d)} disabled={busy}>{busy ? "…" : "Save"}</button>
        <input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@brandcommand.app" style={{ ...input, width: 220, marginTop: 0 }} />
        <button className="secondary" onClick={() => onTest(d.step)} disabled={busy || !testTo} title="Sends the SAVED copy">Send test →</button>
        <span style={{ width: 1, height: 22, background: "var(--border)" }} />
        <select value={copyTo} onChange={(e) => setCopyTo(e.target.value)} style={{ ...input, width: 200, marginTop: 0 }}>
          <option value="">Copy to campaign…</option>
          {campaigns.filter((c) => c.key !== currentKey).map((c) => <option key={c.id} value={c.key}>{c.name}</option>)}
        </select>
        <button className="secondary" onClick={() => { if (copyTo) onCopy(step.id, copyTo); }} disabled={busy || !copyTo}>Copy</button>
        <button className="secondary" onClick={() => onDelete(step.id)} disabled={busy} style={{ color: "var(--error)" }}>Delete step</button>
      </div>
    </div>
  );
}

// ── Campaign detail (steps) ──────────────────────────────────────────────────
function CampaignDetail({ campaign, campaigns, formats, onBack }) {
  const [steps, setSteps] = useState([]);
  const [selId, setSelId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [testTo, setTestTo] = useState("");
  const dragId = useRef(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try { const r = await listTemplates(campaign.key); setSteps(r); setSelId((cur) => (r.find((x) => x.id === cur) ? cur : (r[0]?.id ?? null))); }
    catch (e) { setErr(e.message); } finally { setLoading(false); }
  }, [campaign.key]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { supabase.auth.getUser().then(({ data }) => { if (data?.user?.email) setTestTo((t) => t || data.user.email); }); }, []);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(null), 2500); };
  async function run(fn, ok) { setBusy(true); setErr(null); try { await fn(); if (ok) flash(ok); await load(); } catch (e) { setErr(e.message); } finally { setBusy(false); } }

  const sel = steps.find((s) => s.id === selId);
  const lastStep = steps.length ? Math.max(...steps.map((s) => s.step)) : 0;

  async function onDrop(targetId) {
    const from = dragId.current; dragId.current = null;
    if (!from || from === targetId) return;
    const ids = steps.map((s) => s.id);
    const fi = ids.indexOf(from), ti = ids.indexOf(targetId);
    if (fi < 0 || ti < 0) return;
    ids.splice(ti, 0, ids.splice(fi, 1)[0]);
    await run(() => reorderSteps(ids));
  }

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <button className="secondary" onClick={onBack}>← Campaigns</button>
        <h3 style={{ margin: 0, fontSize: 18, color: "var(--heading)" }}>{campaign.name}</h3>
        <button className="btn" onClick={() => run(async () => { const id = await createStep(campaign.key, campaign.default_template_id); setSelId(id); }, "Step added.")} disabled={busy} style={{ marginLeft: "auto" }}>+ Add step</button>
      </div>
      {err && <div className="admin-banner" role="alert" style={{ marginBottom: 12 }}>{err}</div>}
      {msg && <div style={{ background: "var(--success-soft)", color: "var(--success)", border: "1px solid rgba(31,157,107,.25)", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 14 }}>{msg}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "270px 1fr", gap: 22, alignItems: "start" }}>
        <div style={{ border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden", background: "var(--surface)" }}>
          {loading && <div style={{ padding: 16, color: "var(--muted)", fontSize: 13 }}>Loading…</div>}
          {!loading && steps.length === 0 && <div style={{ padding: 16, color: "var(--muted)", fontSize: 13 }}>No steps yet — add one.</div>}
          {steps.map((r, i) => (
            <div
              key={r.id}
              draggable
              onDragStart={() => { dragId.current = r.id; }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(r.id)}
              onClick={() => setSelId(r.id)}
              style={{ borderBottom: "1px solid var(--hairline)", background: r.id === selId ? "var(--sunken)" : "var(--surface)", padding: "12px 14px", cursor: "pointer", display: "flex", gap: 10, alignItems: "start" }}
            >
              <span title="Drag to reorder" style={{ color: "var(--muted)", cursor: "grab", fontSize: 14, lineHeight: 1.2, userSelect: "none" }}>⋮⋮</span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: "var(--heading)" }}>E{i + 1}</span>
                  <span style={{ fontSize: 10, fontWeight: 700, color: r.is_active ? "var(--success)" : "var(--muted)" }}>{r.is_active ? "ACTIVE" : "OFF"}</span>
                </div>
                <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.subject}</div>
                <div style={{ fontSize: 10.5, color: "var(--muted)", marginTop: 3 }}>{r.step === lastStep ? "last step" : `then wait ${r.interval_days ?? 3}d`}</div>
              </div>
            </div>
          ))}
        </div>

        {sel ? (
          <StepEditor
            step={sel} formats={formats} campaigns={campaigns} currentKey={campaign.key}
            isLast={sel.step === lastStep} busy={busy} testTo={testTo} setTestTo={setTestTo}
            onSave={(d) => run(() => saveTemplate(d.id, d), "Saved.")}
            onDelete={(id) => { if (window.confirm("Delete this step?")) run(() => deleteStep(id)); }}
            onCopy={(id, key) => run(() => copyStepToCampaign(id, key), "Step copied.")}
            onTest={(stepNum) => run(async () => { await sendTestTemplate(campaign.key, stepNum, testTo); }, `Test sent to ${testTo}.`)}
          />
        ) : <div style={{ color: "var(--muted)", fontSize: 14 }}>Select a step to edit.</div>}
      </div>
    </>
  );
}

// ── Root ─────────────────────────────────────────────────────────────────────
export default function NurtureCampaignsPanel() {
  const [campaigns, setCampaigns] = useState([]);
  const [formats, setFormats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try { const [c, f] = await Promise.all([listCampaigns(), listFormats()]); setCampaigns(c); setFormats(f); }
    catch (e) { setErr(e.message); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function onNew() {
    const name = window.prompt("New campaign name:");
    if (!name) return;
    try { const def = formats.find((f) => f.is_default) || formats[0]; await createCampaign({ name, defaultTemplateId: def?.id ?? null }); await load(); }
    catch (e) { setErr(e.message); }
  }
  async function onDelete(c) {
    if (!window.confirm(`Delete the "${c.name}" campaign and all its steps?`)) return;
    try { await deleteCampaign(c.id, c.key); await load(); } catch (e) { setErr(e.message); }
  }

  if (active) {
    const fresh = campaigns.find((c) => c.id === active.id) || active;
    return <CampaignDetail campaign={fresh} campaigns={campaigns} formats={formats} onBack={() => { setActive(null); load(); }} />;
  }
  return (
    <>
      {err && <div className="admin-banner" role="alert" style={{ marginBottom: 12 }}>{err} {/^Could not load/.test(err) ? "— if the nurture tables aren't set up, apply migrations 0047–0051." : ""}</div>}
      <Grid campaigns={campaigns} loading={loading} onOpen={setActive} onNew={onNew} onDelete={onDelete} />
    </>
  );
}
